import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import dayjs, { Dayjs } from 'dayjs';
import SubscriptionOpening from './subscription/SubscriptionOpening';
import SubscriptionTimezone from './subscription/SubscriptionTimezone';
import MessagesCounters from './@shared/MessagesCounters';
import MessagesCountersRegisters from './@shared/messages_counters/MessagesCountersRegisters';
import CountRegister from './@shared/messages_counters/messages_counters_register/CountRegister';
import SubscriptionSettings from './subscription/SubscriptionSettings';
import SubscriptionPartnerSettings from './subscription/SubscriptionPartnerSettings';
import { ICreditMap, ILeedbucksCreditsTemp, ILeedbucksRatesTemp, IScheduledPlanChange, ISubscription } from '@leedpulse-monorepo/interfaces';
import { CostTypeRef, ECreditType, SubscriptionStatus } from '@leedpulse-monorepo/enums';

// TODO : il faut absolument degager cette class du backoffice elle complexifie tout pour rien
// TODO2 : non serieusement ca devient nimp 
export default class Subscription {
  private _id: string;
  private _name: string;
  private _apiKey: string;
  private _credits: ICreditMap;
  private _idAi: string;
  private _idPlan: string;
  private _opening: SubscriptionOpening;
  private _planEnd: Dayjs;
  private _commitmentDate?: number | null; // End of this number's engagement, null when it carries none
  private _creditsResetAt?: number; // Start of the credits cycle the current allocation was granted for
  private _creditsPeriodEnd?: number; // End of the current credits cycle, resolved server-side
  private _planStart: Dayjs;
  private _sender: string[];
  private _timezone: SubscriptionTimezone;
  private _messagesCounters: MessagesCounters;
  private _lastActivity: Dayjs;
  private _settings: SubscriptionSettings;
  private _idScenarios: string[];
  private _employeeInChargeUid: string;
  private _datasource: string;
  private _partnerUid: string;
  private _partnerApiKey: string;
  private _partnerSystem: string;
  private _partnerLabel: string;
  private _partnerSettings: SubscriptionPartnerSettings;
  private _network: string;
  private _status: SubscriptionStatus;
  private _notes: string;
  private _pendingPlanChange: ISubscription['pending_plan_change'];
  private _leedbucksRates: Partial<Record<CostTypeRef, { unit_value: number }>>;
  private _leedbucksRatesTemp: ILeedbucksRatesTemp[];
  private _leedbucksCreditsTemp: ILeedbucksCreditsTemp[];
  private _scheduledPlanChange: IScheduledPlanChange | null;

  constructor(
    id: string,
    name: string,
    apiKey: string,
    credits: ICreditMap,
    idAi: string,
    idPlan: string,
    opening: SubscriptionOpening,
    planEnd: Dayjs,
    planStart: Dayjs,
    sender: string[],
    timezone: SubscriptionTimezone,
    messagesCounters: MessagesCounters,
    lastActivity: Dayjs,
    settings: SubscriptionSettings,
    idScenarios: string[],
    employeeInChargeUid: string,
    datasource: string,
    partnerUid: string,
    partnerApiKey: string,
    partnerSystem: string,
    partnerLabel: string,
    partnerSettings: SubscriptionPartnerSettings,
    network: string,
    status: SubscriptionStatus = SubscriptionStatus.READY,
    notes = '',
    pendingPlanChange: ISubscription['pending_plan_change'] = null,
    creditsPeriodEnd?: number,
    creditsResetAt?: number,
    commitmentDate?: number | null,
    leedbucksRates: Partial<Record<CostTypeRef, { unit_value: number }>> = {},
    leedbucksRatesTemp: ILeedbucksRatesTemp[] = [],
    leedbucksCreditsTemp: ILeedbucksCreditsTemp[] = [],
    scheduledPlanChange: IScheduledPlanChange | null = null,
  ) {
    this._id = id;
    this._name = name;
    this._apiKey = apiKey;
    this._credits = credits;
    this._idAi = idAi;
    this._idPlan = idPlan;
    this._opening = opening;
    this._planEnd = planEnd;
    this._creditsPeriodEnd = creditsPeriodEnd;
    this._creditsResetAt = creditsResetAt;
    this._commitmentDate = commitmentDate;
    this._planStart = planStart;
    this._sender = sender;
    this._timezone = timezone;
    this._messagesCounters = messagesCounters;
    this._lastActivity = lastActivity;
    this._settings = settings;
    this._idScenarios = idScenarios;
    this._employeeInChargeUid = employeeInChargeUid;
    this._datasource = datasource;
    this._partnerUid = partnerUid;
    this._partnerApiKey = partnerApiKey;
    this._partnerSystem = partnerSystem;
    this._partnerLabel = partnerLabel;
    this._partnerSettings = partnerSettings;
    this._network = network;
    this._status = status;
    this._notes = notes;
    this._pendingPlanChange = pendingPlanChange;
    this._leedbucksRates = leedbucksRates;
    this._leedbucksRatesTemp = leedbucksRatesTemp;
    this._leedbucksCreditsTemp = leedbucksCreditsTemp;
    this._scheduledPlanChange = scheduledPlanChange;
  }

  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get apiKey(): string {
    return this._apiKey;
  }

  get credits(): ICreditMap {
    return this._credits;
  }

  get idAi(): string {
    return this._idAi;
  }

  get idPlan(): string {
    return this._idPlan;
  }

  get opening(): SubscriptionOpening {
    return this._opening;
  }

  get planEnd(): Dayjs {
    return this._planEnd;
  }

  get creditsPeriodEnd(): number | undefined {
    return this._creditsPeriodEnd;
  }

  get creditsResetAt(): number | undefined {
    return this._creditsResetAt;
  }

  get commitmentDate(): number | null | undefined {
    return this._commitmentDate;
  }

  get planStart(): Dayjs {
    return this._planStart;
  }

  get sender(): string[] {
    return this._sender;
  }

  get timezone(): SubscriptionTimezone {
    return this._timezone;
  }

  get messagesCounters(): MessagesCounters {
    return this._messagesCounters;
  }

  get unreadAssistantMessagesCount(): number {
    return this._messagesCounters.backoffice.assistant.unread;
  }

  get unreadUserMessagesCount(): number {
    return this._messagesCounters.backoffice.user.unread;
  }

  get lastActivity(): Dayjs {
    return this._lastActivity;
  }

  get settings(): SubscriptionSettings {
    return this._settings;
  }

  get idScenarios(): string[] {
    return this._idScenarios;
  }

  get employeeInChargeUid(): string {
    return this._employeeInChargeUid;
  }

  get datasource(): string {
    return this._datasource;
  }

  get partnerUid(): string {
    return this._partnerUid;
  }

  get partnerApiKey(): string {
    return this._partnerApiKey;
  }

  get partnerSystem(): string {
    return this._partnerSystem;
  }

  get partnerLabel(): string {
    return this._partnerLabel;
  }

  get partnerSettings(): SubscriptionPartnerSettings {
    return this._partnerSettings;
  }

  get network(): string {
    return this._network;
  }

  get status(): SubscriptionStatus {
    return this._status;
  }

  get notes(): string {
    return this._notes;
  }

  /** Leedflow downsell scheduled for the next billing date — read-only, managed by the Stripe flow */
  get pendingPlanChange(): ISubscription['pending_plan_change'] {
    return this._pendingPlanChange;
  }

  get leedbucksRates(): Partial<Record<CostTypeRef, { unit_value: number }>> {
    return this._leedbucksRates;
  }

  get leedbucksRatesTemp(): ILeedbucksRatesTemp[] {
    return this._leedbucksRatesTemp;
  }

  get leedbucksCreditsTemp(): ILeedbucksCreditsTemp[] {
    return this._leedbucksCreditsTemp;
  }

  /** Plan change set for a later day from the backoffice, read-only: the backend writes it */
  get scheduledPlanChange(): IScheduledPlanChange | null {
    return this._scheduledPlanChange;
  }

  public copyWith({
    name,
    credits,
    idPlan,
    opening,
    planEnd,
    planStart,
    sender,
    timezone,
    messagesCounters,
    lastActivity,
    settings,
    idScenarios,
    employeeInChargeUid,
    datasource,
    partnerUid,
    partnerApiKey,
    partnerSystem,
    partnerLabel,
    partnerSettings,
    network,
    status,
    notes,
    commitmentDate,
    creditsResetAt,
    creditsPeriodEnd,
    leedbucksRates,
    leedbucksRatesTemp,
    leedbucksCreditsTemp,
  }: {
    name?: string;
    credits?: ICreditMap;
    idPlan?: string;
    opening?: SubscriptionOpening;
    planEnd?: Dayjs;
    planStart?: Dayjs;
    sender?: string[];
    timezone?: SubscriptionTimezone;
    messagesCounters?: MessagesCounters;
    lastActivity?: Dayjs;
    settings?: SubscriptionSettings;
    idScenarios?: string[];
    employeeInChargeUid?: string;
    datasource?: string;
    partnerUid?: string;
    partnerApiKey?: string;
    partnerSystem?: string;
    partnerLabel?: string;
    partnerSettings?: SubscriptionPartnerSettings;
    network?: string;
    status?: SubscriptionStatus;
    notes?: string;
    commitmentDate?: number | null;
    creditsResetAt?: number;
    creditsPeriodEnd?: number;
    leedbucksRates?: Partial<Record<CostTypeRef, { unit_value: number }>>;
    leedbucksRatesTemp?: ILeedbucksRatesTemp[];
    leedbucksCreditsTemp?: ILeedbucksCreditsTemp[];
  }): Subscription {
    return new Subscription(
      this._id,
      name ?? this._name,
      this._apiKey,
      credits ?? this._credits,
      this._idAi,
      idPlan ?? this._idPlan,
      opening ?? this._opening,
      planEnd ?? this._planEnd,
      planStart ?? this._planStart,
      sender ?? this._sender,
      timezone ?? this._timezone,
      messagesCounters ?? this._messagesCounters,
      lastActivity ?? this._lastActivity,
      settings ?? this._settings,
      idScenarios ?? this._idScenarios,
      employeeInChargeUid ?? this.employeeInChargeUid,
      datasource ?? this._datasource,
      partnerUid ?? this._partnerUid,
      partnerApiKey ?? this._partnerApiKey,
      partnerSystem ?? this._partnerSystem,
      partnerLabel ?? this._partnerLabel,
      partnerSettings ?? this._partnerSettings,
      network ?? this._network,
      status ?? this._status,
      notes ?? this._notes,
      this._pendingPlanChange,
      creditsPeriodEnd ?? this._creditsPeriodEnd,
      creditsResetAt ?? this._creditsResetAt,
      commitmentDate !== undefined ? commitmentDate : this._commitmentDate,
      leedbucksRates ?? this._leedbucksRates,
      leedbucksRatesTemp ?? this._leedbucksRatesTemp,
      leedbucksCreditsTemp ?? this._leedbucksCreditsTemp,
      this._scheduledPlanChange,
    );
  }

  static fromFirestore(
    doc: QueryDocumentSnapshot<DocumentData, DocumentData>
  ): Subscription {
    const docData = doc.data();

    return new Subscription(
      doc.id,
      docData.name,
      docData.api_key,
      docData.credits,
      docData.id_ai,
      docData.id_plan,
      SubscriptionOpening.fromFirestoreMap(docData.opening),
      dayjs(docData.plan_end),
      dayjs(docData.plan_start),
      docData.sender,
      SubscriptionTimezone.fromFirestoreMap(docData.timezone),
      docData.messages_counters
        ? MessagesCounters.fromFirestoreMap(docData.messages_counters)
        : new MessagesCounters(
            new MessagesCountersRegisters(
              new CountRegister(0, 0),
              new CountRegister(0, 0)
            ),
            new MessagesCountersRegisters(
              new CountRegister(0, 0),
              new CountRegister(0, 0)
            )
          ),
      dayjs(docData.last_activity),
      docData.settings,
      docData.id_scenarios,
      docData.employee_incharge_uid,
      docData.datasource,
      docData.partner_uid || '',
      docData.partner_api_key || '',
      docData.partner_system || '',
      docData.partner_label || '',
      docData.partner_settings,
      docData.network || '',
      docData.status as SubscriptionStatus,
      docData.notes || '',
      docData.pending_plan_change || null,
      docData.credits_period_end,
      docData.credits_reset_at,
      docData.commitment_date,
      docData.leedbucks_rates,
      docData.leedbucks_rates_temp,
      docData.leedbucks_credits_temp,
      docData.scheduled_plan_change || null,
    );
  }

  static fromJson(json: ISubscription): Subscription {
    return new Subscription(
      json.id,
      json.name,
      json.api_key,
      json.credits,
      json.id_ai,
      json.id_plan,
      SubscriptionOpening.fromFirestoreMap(json.opening),
      dayjs(json.plan_end),
      dayjs(json.plan_start),
      json.sender,
      SubscriptionTimezone.fromFirestoreMap(json.timezone),
      MessagesCounters.fromFirestoreMap(json.messages_counters),
      dayjs(json.last_activity),
      json.settings,
      json.id_scenarios,
      json.employee_incharge_uid,
      json.datasource,
      json.partner_uid || '',
      json.partner_api_key || '',
      json.partner_system || '',
      json.partner_label || '',
      json.partner_settings,
      json.network || '',
      json.status as SubscriptionStatus,
      json.notes || '',
      json.pending_plan_change || null,
      json.credits_period_end,
      json.credits_reset_at,
      json.commitment_date,
      json.leedbucks_rates,
      json.leedbucks_rates_temp,
      json.leedbucks_credits_temp,
      json.scheduled_plan_change || null,
    );
  }

  toFirestorePatch(): Record<string, any> {
    return {
      id: this._id,
      name: this._name,
      // Never the leedbucks: the whole map put back the balance loaded with the page, refunding
      // everything consumed since. The balance only moves through the traced backend routes.
      ...Object.fromEntries(
        Object.entries(this._credits)
          .filter(([key, value]) => key !== ECreditType.leedbucks && value !== undefined)
          .map(([key, value]) => [`credits.${key}`, value])
      ),
      id_plan: this._idPlan,
      opening: this._opening.toFirestoreMapPatch(),
      plan_end: this._planEnd.valueOf(),
      // Only when set: `updateDoc` rejects an undefined value, and these three are absent on
      // every subscription that predates them — writing them blindly broke every save.
      ...(this._creditsPeriodEnd !== undefined && { credits_period_end: this._creditsPeriodEnd }),
      ...(this._creditsResetAt !== undefined && { credits_reset_at: this._creditsResetAt }),
      ...(this._commitmentDate !== undefined && { commitment_date: this._commitmentDate }),
      // plan_start not written, the backend re-syncs it on each invoice
      sender: this._sender,
      timezone: this._timezone.toMapPatch(),
      settings: this._settings,
      id_scenarios: this._idScenarios,
      employee_incharge_uid: this._employeeInChargeUid,
      datasource: this._datasource,
      partner_uid: this._partnerUid,
      partner_api_key: this._partnerApiKey,
      partner_system: this._partnerSystem,
      partner_label: this._partnerLabel,
      partner_settings: this._partnerSettings,
      network: this._network,
      notes: this._notes,
      leedbucks_rates: this._leedbucksRates,
      leedbucks_rates_temp: this._leedbucksRatesTemp,
      leedbucks_credits_temp: this._leedbucksCreditsTemp,
    };
  }
}
